import { appConfigSchema, type AppConfig } from "@noa/ui";
import { z } from "zod";

/**
 * Deployment configuration, served next to the bundle rather than compiled into it: a deployment
 * changes the product name, tagline and quick prompts by shipping its own copy of this file, with
 * no rebuild. See `apps/frontend/public/environment/app-configs.json` for the shape.
 */
export const APP_CONFIGS_URL = "/environment/app-configs.json";

/**
 * Fetches and validates the file. Throws on a network error, a non-2xx status, malformed JSON or a
 * shape the app cannot use — the file is hand-editable, so `res.json()` alone proves nothing.
 */
export const fetchAppConfigs = async (): Promise<AppConfig> => {
  const response = await fetch(APP_CONFIGS_URL);
  if (!response.ok) throw new Error(`HTTP ${response.status}`);

  const parsed = appConfigSchema.safeParse(await response.json());
  if (!parsed.success) throw new Error(`unexpected shape:\n${z.prettifyError(parsed.error)}`);
  return parsed.data;
};

/**
 * Loads the file during bootstrap, before i18n is initialized, so the branded strings are in place
 * for the first render — a later override would repaint the header.
 *
 * Never throws: a deployment that forgot or broke the file still gets a working app on the strings
 * bundled in @noa/i18n, and `useAppConfigs` re-fetches it (and surfaces the error) for the quick
 * prompts.
 */
export const loadAppConfigs = async (): Promise<AppConfig | undefined> => {
  try {
    return await fetchAppConfigs();
  } catch (error) {
    console.error(`[config] ${APP_CONFIGS_URL} unusable — falling back to the bundled defaults`, error);
    return undefined;
  }
};
